import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { AdminButton } from "@/components/admin/button";
import { Card } from "@/components/admin/card";
import { ChipFilter } from "@/components/admin/chip-filter";
import { AdminIcon } from "@/components/admin/icon";
import { PageHead } from "@/components/admin/page-head";
import { Tag } from "@/components/admin/tag";
import {
	type QuoteRequest,
	type QuoteStatus,
	quotesApi,
} from "@/lib/quotes/quotes-api";
import type { TagClass } from "@/lib/status";

const STATUS_LABEL: Record<QuoteStatus, string> = {
	new: "Nouveau",
	in_progress: "En cours",
	quoted: "Devis envoyé",
	won: "Gagné",
	lost: "Perdu",
};
const STATUS_CLASS: Record<QuoteStatus, TagClass> = {
	new: "info",
	in_progress: "warning",
	quoted: "ochre",
	won: "success",
	lost: "neutral",
};
const STATUSES: QuoteStatus[] = ["new", "in_progress", "quoted", "won", "lost"];

const STATUS_FILTERS: Array<{ id: QuoteStatus | "all"; label: string }> = [
	{ id: "all", label: "Tous" },
	{ id: "new", label: "Nouveaux" },
	{ id: "in_progress", label: "En cours" },
	{ id: "quoted", label: "Devis envoyés" },
	{ id: "won", label: "Gagnés" },
	{ id: "lost", label: "Perdus" },
];

function fmtDate(s: string) {
	return new Date(s).toLocaleString("fr-FR", {
		day: "2-digit",
		month: "2-digit",
		year: "2-digit",
		hour: "2-digit",
		minute: "2-digit",
	});
}

export function AdminQuotesPage() {
	const qc = useQueryClient();
	const [filter, setFilter] = useState<QuoteStatus | "all">("all");
	const [search, setSearch] = useState("");
	const [selectedId, setSelectedId] = useState<string | null>(null);
	const [note, setNote] = useState("");

	const { data: stats } = useQuery({
		queryKey: ["quotes", "stats"],
		queryFn: () => quotesApi.stats(),
	});
	const { data: list } = useQuery({
		queryKey: ["quotes", "list", filter, search],
		queryFn: () =>
			quotesApi.list({
				...(filter === "all" ? {} : { status: filter }),
				q: search || undefined,
				pageSize: 100,
			}),
	});
	const quotes = list?.data ?? [];
	const selected: QuoteRequest | undefined = quotes.find(
		(q) => q.id === selectedId,
	);

	useEffect(() => {
		setNote(selected?.adminNote ?? "");
	}, [selectedId, selected?.adminNote]);

	const invalidate = () => {
		void qc.invalidateQueries({ queryKey: ["quotes"] });
	};
	const updateMut = useMutation({
		mutationFn: ({
			id,
			status,
			adminNote,
		}: {
			id: string;
			status?: QuoteStatus;
			adminNote?: string;
		}) => quotesApi.update(id, { status, adminNote }),
		onSuccess: invalidate,
	});

	return (
		<>
			<PageHead
				title="Demandes de devis"
				sub={`${stats?.new ?? 0} nouvelles · ${stats?.in_progress ?? 0} en cours · ${stats?.total ?? 0} au total`}
			/>

			<div
				className="kpi-grid"
				style={{ gridTemplateColumns: "repeat(6, 1fr)" }}
			>
				<div className="kpi accent">
					<div className="kpi-l">Nouvelles</div>
					<div className="kpi-v tn">{stats?.new ?? 0}</div>
				</div>
				<div className="kpi warning">
					<div className="kpi-l">En cours</div>
					<div className="kpi-v tn">{stats?.in_progress ?? 0}</div>
				</div>
				<div className="kpi">
					<div className="kpi-l">Devis envoyés</div>
					<div className="kpi-v tn">{stats?.quoted ?? 0}</div>
				</div>
				<div className="kpi success">
					<div className="kpi-l">Gagnés</div>
					<div className="kpi-v tn">{stats?.won ?? 0}</div>
				</div>
				<div className="kpi">
					<div className="kpi-l">Perdus</div>
					<div className="kpi-v tn">{stats?.lost ?? 0}</div>
				</div>
				<div className="kpi forest">
					<div className="kpi-l">Total</div>
					<div className="kpi-v tn">{stats?.total ?? 0}</div>
				</div>
			</div>

			<Card>
				<div className="toolbar" style={{ gap: 12, alignItems: "center" }}>
					<div className="filter-bar" style={{ marginBottom: 0 }}>
						{STATUS_FILTERS.map((f) => (
							<ChipFilter
								key={f.id}
								on={filter === f.id}
								onClick={() => setFilter(f.id)}
							>
								{f.label}
							</ChipFilter>
						))}
					</div>
					<input
						className="field-input"
						style={{ maxWidth: 240, marginLeft: "auto" }}
						placeholder="Référence, nom, téléphone…"
						value={search}
						onChange={(e) => setSearch(e.target.value)}
					/>
				</div>
				<div className="table-wrap" style={{ margin: 0 }}>
					<table className="table">
						<thead>
							<tr>
								<th>Référence</th>
								<th>Client</th>
								<th>Ville</th>
								<th>Produits</th>
								<th>Reçue le</th>
								<th>Statut</th>
								<th />
							</tr>
						</thead>
						<tbody>
							{quotes.length === 0 && (
								<tr>
									<td
										colSpan={7}
										className="muted"
										style={{ textAlign: "center", padding: 24 }}
									>
										Aucune demande de devis.
									</td>
								</tr>
							)}
							{quotes.map((q) => (
								<tr key={q.id}>
									<td>
										<span className="mono">{q.reference}</span>
									</td>
									<td>
										<strong style={{ fontSize: 12 }}>{q.customerName}</strong>
										<div className="muted" style={{ fontSize: 11 }}>
											{q.customerPhone}
										</div>
									</td>
									<td>{q.customerCity ?? <span className="muted">—</span>}</td>
									<td>
										<strong className="tn">{q.itemCount}</strong>
										<div
											className="muted"
											style={{
												fontSize: 11,
												maxWidth: 260,
												overflow: "hidden",
												textOverflow: "ellipsis",
												whiteSpace: "nowrap",
											}}
										>
											{q.items.map((i) => i.title).join(", ")}
										</div>
									</td>
									<td className="tn" style={{ fontSize: 12 }}>
										{fmtDate(q.createdAt)}
									</td>
									<td>
										<select
											className="field-input"
											style={{ padding: "4px 8px", fontSize: 12 }}
											value={q.status}
											onChange={(e) =>
												updateMut.mutate({
													id: q.id,
													status: e.target.value as QuoteStatus,
												})
											}
										>
											{STATUSES.map((s) => (
												<option key={s} value={s}>
													{STATUS_LABEL[s]}
												</option>
											))}
										</select>
									</td>
									<td>
										<AdminButton
											variant="ghost"
											onClick={() =>
												setSelectedId(selectedId === q.id ? null : q.id)
											}
										>
											<AdminIcon n="eye" s={14} />
											Détail
										</AdminButton>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</Card>

			{selected && (
				<Card>
					<div
						style={{
							padding: 18,
							display: "grid",
							gap: 16,
							gridTemplateColumns: "1.2fr 1fr",
						}}
					>
						<div>
							<div
								style={{
									display: "flex",
									alignItems: "center",
									gap: 10,
									marginBottom: 12,
								}}
							>
								<span
									className="mono"
									style={{ fontSize: 14, fontWeight: 700 }}
								>
									{selected.reference}
								</span>
								<Tag variant={STATUS_CLASS[selected.status]}>
									{STATUS_LABEL[selected.status]}
								</Tag>
								<span
									className="muted"
									style={{ fontSize: 12, marginLeft: "auto" }}
								>
									{fmtDate(selected.createdAt)}
								</span>
							</div>
							<div
								style={{
									display: "grid",
									gap: 4,
									marginBottom: 14,
									fontSize: 13,
								}}
							>
								<div>
									<strong>{selected.customerName}</strong>
								</div>
								<div className="muted">
									{selected.customerPhone}
									{selected.customerEmail ? ` · ${selected.customerEmail}` : ""}
								</div>
								<div className="muted">{selected.customerCity ?? "—"}</div>
							</div>
							{selected.message && (
								<div style={{ marginBottom: 14 }}>
									<div
										className="muted"
										style={{ fontSize: 11, marginBottom: 4 }}
									>
										Message du client
									</div>
									<p style={{ margin: 0, fontSize: 13, lineHeight: 1.5 }}>
										{selected.message}
									</p>
								</div>
							)}
							<div className="muted" style={{ fontSize: 11, marginBottom: 6 }}>
								Produits demandés
							</div>
							<div style={{ display: "grid", gap: 8 }}>
								{selected.items.map((it) => (
									<div
										key={it.productId}
										style={{ display: "flex", alignItems: "center", gap: 10 }}
									>
										{it.heroImage ? (
											<img
												src={it.heroImage}
												alt=""
												style={{
													width: 40,
													height: 40,
													objectFit: "cover",
													borderRadius: 8,
													background: "var(--a-bg-deep)",
												}}
											/>
										) : (
											<div
												style={{
													width: 40,
													height: 40,
													borderRadius: 8,
													background: "var(--a-bg-deep)",
												}}
											/>
										)}
										<div style={{ flex: 1, fontSize: 13 }}>
											<strong>{it.title}</strong>
											{it.brand && <span className="muted"> · {it.brand}</span>}
										</div>
										<span className="tn" style={{ fontSize: 13 }}>
											× {it.qty}
										</span>
									</div>
								))}
							</div>
						</div>
						<div style={{ display: "grid", gap: 12, alignContent: "start" }}>
							<label style={{ display: "grid", gap: 4 }}>
								<span className="muted" style={{ fontSize: 11 }}>
									Statut
								</span>
								<select
									className="field-input"
									value={selected.status}
									onChange={(e) =>
										updateMut.mutate({
											id: selected.id,
											status: e.target.value as QuoteStatus,
										})
									}
								>
									{STATUSES.map((s) => (
										<option key={s} value={s}>
											{STATUS_LABEL[s]}
										</option>
									))}
								</select>
							</label>
							<label style={{ display: "grid", gap: 4 }}>
								<span className="muted" style={{ fontSize: 11 }}>
									Note interne
								</span>
								<textarea
									className="field-input"
									rows={5}
									value={note}
									onChange={(e) => setNote(e.target.value)}
									placeholder="Devis envoyé par WhatsApp, relance prévue…"
									style={{ resize: "none" }}
								/>
							</label>
							<AdminButton
								variant="primary"
								disabled={updateMut.isPending}
								onClick={() =>
									updateMut.mutate({ id: selected.id, adminNote: note })
								}
							>
								{updateMut.isPending ? "…" : "Enregistrer la note"}
							</AdminButton>
						</div>
					</div>
				</Card>
			)}
		</>
	);
}
